import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Building2, User, Lock } from 'lucide-react';
import { toast } from 'sonner';

export default function EmployeeLogin() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { employeeLogin } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        company_slug: searchParams.get('c') || '',
        username: '',
        password: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await employeeLogin(formData.company_slug, formData.username, formData.password);
            toast.success('Welcome!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-900 transition-colors mb-8"
                    data-testid="back-link"
                >
                    <ArrowLeft size={20} />
                    <span>Back to home</span>
                </Link>

                <div className="card p-8">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Employee Login</h1>
                        <p className="text-gray-500">Sign in to your company workspace</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="form-label">Company Code</label>
                            <div className="relative">
                                <Building2 className="form-icon" size={18} />
                                <input
                                    type="text"
                                    value={formData.company_slug}
                                    onChange={(e) => setFormData(prev => ({ ...prev, company_slug: e.target.value.toLowerCase() }))}
                                    placeholder="acme-factory"
                                    className="form-input form-input-icon"
                                    required
                                    data-testid="company-slug-input"
                                />
                            </div>
                            <p className="text-xs text-gray-400 mt-1">Ask your manager for the company code</p>
                        </div>

                        <div>
                            <label className="form-label">Username</label>
                            <div className="relative">
                                <User className="form-icon" size={18} />
                                <input
                                    type="text"
                                    value={formData.username}
                                    onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                    placeholder="johndoe"
                                    className="form-input form-input-icon"
                                    required
                                    data-testid="username-input"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="form-label">Password</label>
                            <div className="relative">
                                <Lock className="form-icon" size={18} />
                                <input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                    placeholder="••••••••"
                                    className="form-input form-input-icon"
                                    required
                                    data-testid="password-input"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn btn-primary w-full py-3 mt-6"
                            data-testid="login-btn"
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>
                    </form>

                    <p className="text-center text-gray-500 mt-6">
                        Are you an owner?{' '}
                        <Link to="/signin" className="text-gray-900 hover:underline font-medium" data-testid="owner-signin-link">
                            Sign in here
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
